'use client';

import { useDrop } from 'ahooks';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { ReactSortable } from 'react-sortablejs';
import { twMerge } from 'tailwind-merge';
import { Button, CruzIcon } from '@/components';
import { acceptImageTypes } from '@/settings';
type Props = {
  id?: string;
  'data-error'?: string | React.ReactNode;
  onChange: (files: File[]) => void;
  isSaved?: boolean;
};
type MyFile = File & {
  id: string;
  selected?: boolean;
  preview?: string;
};
export function PhotoUpload(props: Props) {
  const {
    onChange,
    isSaved
  } = props;
  const errMsg = _.get(props, 'data-error');
  useEffect(() => {
    if (isSaved) {
      setPhotos([]);
    }
  }, [isSaved]);
  const [photos, setPhotos] = useState<MyFile[]>([]);
  useEffect(() => {
    onChange(photos);
  }, [photos]);

  // select file
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const selectFile = () => {
    fileInputRef.current?.click();
  };
  const selectFileChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const d = _.filter(e.target.files, x => acceptImageTypes.includes(x.type)) as MyFile[];
      _.forEach(d, x_0 => {
        x_0.id = _.uniqueId();
        x_0.preview = window.URL.createObjectURL(x_0);
      });
      setPhotos([...photos, ...d]);
    }
  };

  // drop file
  const [isHovering, setIsHovering] = useState(false);
  const dropRef = useRef(null);
  useDrop(dropRef, {
    onFiles: files => {
      const d_0 = files.filter(x_1 => acceptImageTypes.includes(x_1.type)) as MyFile[];
      _.forEach(d_0, x_2 => {
        x_2.id = _.uniqueId();
        x_2.preview = window.URL.createObjectURL(x_2);
      });
      setPhotos([...photos, ...d_0]);
      setIsHovering(false);
    },
    // onUri: (uri) => {
    //   setIsHovering(false)
    // },
    onDragEnter: () => setIsHovering(true),
    onDragLeave: () => setIsHovering(false)
  });

  // selected photos
  const [countSelected, setCountSelected] = useState(0);
  function toggleSelectPhoto(id: string) {
    const file = _.find(photos, {
      id
    });
    if (!file) return;
    file.selected = !file.selected;
    setPhotos([...photos]);
    setCountSelected(_.filter(photos, 'selected').length);
  }
  function deleteSelectedPhotos() {
    setPhotos(_.filter(photos, x_3 => !x_3.selected));
    setCountSelected(0);
  }
  function unSelectPhotos() {
    setPhotos(_.map(photos, x_4 => ({
      ...x_4,
      selected: false
    })));
    setCountSelected(0);
  }
  const SelectButton = () => <Button type='button' className='h-8 rounded-full pr-4 pl-3' onClick={selectFile} aria-label='Select photos to upload'>
      <CruzIcon.Plus aria-hidden='true' />
      Select
    </Button>;
  const inputId = props.id || 'photo-upload';
  const errorId = `${inputId}-error`;
  return <section ref={dropRef} className={twMerge('dropzone', isHovering && 'border-zinc-400 shadow-lg dark:border-zinc-500', !!errMsg && 'ring-1 ring-red-800')} aria-label='Photo upload area'>
      <div className='py-3 text-center text-base/8 font-normal' aria-live='polite'>
        <input id={inputId} type='file' multiple ref={fileInputRef} className='hidden' accept={acceptImageTypes.join(',')} onChange={selectFileChanged} aria-invalid={!!errMsg} aria-describedby={errMsg ? errorId : undefined} title='Upload photos' />
        To upload new photo(s), please <SelectButton /> or <span className='font-semibold'>drag & drop</span> your photo(s) here
        {errMsg && <div id={errorId} className='form-error-message mt-4 text-base/6!' aria-live='polite'>
            {errMsg}
          </div>}
      </div>
      <ReactSortable animation={400} list={photos} setList={setPhotos} className='grid w-full grid-cols-4 items-center gap-2 md:grid-cols-3 xl:grid-cols-4' aria-label='Uploaded photos'>
        {_.map(photos, x_5 => {
        return <button type='button' key={x_5.id} onClick={() => toggleSelectPhoto(x_5.id)} className={twMerge('relative rounded-md border py-1', x_5.selected && 'bg-red-800')} aria-label={`Select ${x_5.name}`} aria-pressed={x_5.selected}>
              <img alt={`Preview of uploaded file ${x_5.name}`} src={x_5?.preview} className='m-auto aspect-16/10 object-contain' />
            </button>;
      })}
      </ReactSortable>
      {countSelected > 0 && <div className='mt-4 flex items-center gap-x-2' aria-live='polite'>
          <Button type='button' variant='outline' onClick={unSelectPhotos} className='rounded-full px-6'>
            Cancel
          </Button>
          <Button type='button' variant='secondary' onClick={deleteSelectedPhotos} className='rounded-full px-6' aria-label={`Remove ${countSelected} selected photos`}>
            <CruzIcon.Trash aria-hidden='true' />
            Remove
          </Button>
        </div>}
    </section>;
}